import "@/styles/globals.css"

import { useState, type ReactElement, type ReactNode } from "react"
import type { NextPage } from "next"
import NextApp, {
  type AppContext,
  type AppInitialProps,
  type AppProps,
} from "next/app"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"

import { ClerkThemeProvider } from "@/app-shell/components/ClerkThemeProvider"
import { SkipToMainLink } from "@/app-shell/components/SkipToMainLink"
import { ThemeShortcut } from "@/app-shell/components/ThemeShortcut"
import { AppLocationRecorder } from "@/app-shell/navigation/lastAppLocation"
import { AppShellDomainContributionsProvider } from "@/navigation/routes/AppShellDomainContributionsRoute"
import { Toaster, TooltipProvider } from "@coval/ui"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ThemeProvider as NextThemesProvider } from "next-themes"

import "ag-grid-community/styles/ag-grid.css"
import "ag-grid-community/styles/ag-theme-alpine.css"
import "@/styles/ag-grid-custom.css"
import "@/styles/split-gutter.css"

import { AUTH_PATHS } from "@/constants/auth-routes"

import { DatadogIdentify, PostHogIdentify } from "@/lib/analytics"
import { AppErrorBoundary } from "@/components/error/AppErrorBoundary"
import { BrowserTabTitle } from "@/components/shared/BrowserTabTitle"
import { BreadcrumbTitleProvider } from "@/contexts/BreadcrumbTitleContext"
import { CommandRegistryProvider } from "@/contexts/CommandRegistryContext"
import { HeaderActionsProvider } from "@/contexts/HeaderActionsContext"
import { KeyboardShortcutsProvider } from "@/contexts/KeyboardShortcutsContext"
import { PageHeaderProvider } from "@/contexts/PageHeaderContext"

import "@/lib/datadog"

const ReactQueryDevtools = dynamic(
  () =>
    import("@tanstack/react-query-devtools").then(
      (mod) => mod.ReactQueryDevtools,
    ),
  { ssr: false },
)

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
  /**
   * Names the page in the browser tab. It renders outside the Clerk provider,
   * so the title is set while Clerk loads.
   */
  pageTitle?: string
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
  nonce?: string
}

function App({ Component, pageProps, nonce }: AppPropsWithLayout) {
  const router = useRouter()
  const [queryClient] = useState(() => new QueryClient())
  // Client navigation keeps the policy and nonce of the original document.
  const [documentNonce] = useState(nonce)

  // Default: pages have padding unless they use NoPadding layout
  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <AppErrorBoundary level="app">
      <NextThemesProvider
        attribute="class"
        defaultTheme="light"
        nonce={documentNonce}
      >
        <SkipToMainLink />
        {Component.pageTitle ? (
          <BrowserTabTitle type={Component.pageTitle} />
        ) : null}
        <ClerkThemeProvider
          {...pageProps}
          nonce={documentNonce}
          afterSignOutUrl={AUTH_PATHS.SIGN_IN}
          fallbackRedirectUrl="/home"
          signInUrl={AUTH_PATHS.SIGN_IN}
          signUpUrl={AUTH_PATHS.SIGN_UP}
        >
          <QueryClientProvider client={queryClient}>
            {/*
              The app's only TooltipProvider. It sits above
              `AppShellDomainContributionsProvider` so the Covi panel that
              mounts there is inside it, along with every page. The delays it
              applies live in `@coval/ui`'s `Tooltip.tsx`.
            */}
            <TooltipProvider>
              <AppShellDomainContributionsProvider>
                <AppLocationRecorder />
                <PostHogIdentify />
                <DatadogIdentify />
                <KeyboardShortcutsProvider>
                  <ThemeShortcut />
                  <CommandRegistryProvider>
                    <PageHeaderProvider>
                      <HeaderActionsProvider>
                        <BreadcrumbTitleProvider>
                          <Toaster />
                          <ReactQueryDevtools />
                          <AppErrorBoundary
                            level="route"
                            resetKey={router.asPath}
                          >
                            {getLayout(<Component {...pageProps} />)}
                          </AppErrorBoundary>
                        </BreadcrumbTitleProvider>
                      </HeaderActionsProvider>
                    </PageHeaderProvider>
                  </CommandRegistryProvider>
                </KeyboardShortcutsProvider>
              </AppShellDomainContributionsProvider>
            </TooltipProvider>
          </QueryClientProvider>
        </ClerkThemeProvider>
      </NextThemesProvider>
    </AppErrorBoundary>
  )
}

// A strict nonce-based CSP requires every HTML document to be rendered for the
// incoming request. This intentionally opts Pages Router screens out of
// automatic static optimization so the proxy nonce and rendered scripts match.
App.getInitialProps = async (
  appContext: AppContext,
): Promise<AppInitialProps & { nonce?: string }> => {
  const initialProps = await NextApp.getInitialProps(appContext)
  const rawNonce = appContext.ctx.req?.headers["x-nonce"]
  const nonce = Array.isArray(rawNonce) ? rawNonce[0] : rawNonce

  return { ...initialProps, nonce }
}

export default App
